// 📋 신청 정보 탭 - 상세 정보 표시
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Divider,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  DevicesOther as DeviceIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  PrivacyTip as PrivacyIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  LocalShipping as ShippingIcon,
  Launch as LaunchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Description as ContractIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Link as LinkIcon,
  Notes as NotesIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
  CreditCard as CreditCardIcon,
  Save as SaveIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import DaumPostcode from 'react-daum-postcode';

import type { Application } from '../../types/application';
import { formatDate } from '../orderDetail/utils';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { generateContractPDF, downloadPDF } from '../../../../utils/contractPdfGenerator';
import { isContractFeatureAvailable, isWearable } from '../../../../utils/deviceCategoryUtils';
import { getInflowLabel, getInflowColor } from '../../../../constants/inflowChannels';

interface ApplicationInfoTabProps {
  application: Application;
  onUpdate?: (updatedApplication: Application) => void; // 🆕 상태 업데이트 콜백 (화면 유지)
}

// 📝 정보 섹션 컴포넌트
interface InfoSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  color?: string;
  highlight?: boolean; // 🆕 하이라이트 여부 (정보 미입력 시)
  highlightMessage?: string; // 🆕 하이라이트 메시지
  onEdit?: () => void; // 🆕 수정 버튼 클릭 핸들러
  editLabel?: string; // 🆕 수정 버튼 레이블
}

const InfoSection: React.FC<InfoSectionProps> = ({ 
  title, 
  icon, 
  children, 
  color = '#1976d2',
  highlight = false,
  highlightMessage,
  onEdit,
  editLabel = '수정',
}) => (
  <Card
    elevation={highlight ? 3 : 0}
    sx={{
      border: highlight ? '2px solid #f59e0b' : '1px solid #e1e8ed',
      borderRadius: 3,
      overflow: 'hidden',
      height: '100%',
      position: 'relative',
      // 🆕 하이라이트 효과
      ...(highlight && {
        boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)',
        animation: 'pulse-border 2s infinite',
        '@keyframes pulse-border': {
          '0%, 100%': { boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)' },
          '50%': { boxShadow: '0 4px 30px rgba(245, 158, 11, 0.4)' },
        },
      }),
    }}
  >
    <CardContent sx={{ p: 0 }}>
      {/* 🆕 하이라이트 배너 */}
      {highlight && highlightMessage && (
        <Box
          sx={{
            px: 2.5,
            py: 1,
            bgcolor: '#fef3c7',
            borderBottom: '1px solid #fcd34d',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningIcon sx={{ fontSize: 18, color: '#d97706' }} />
          <Typography variant="caption" fontWeight={600} color="#92400e">
            {highlightMessage}
          </Typography>
        </Box>
      )}
      
      {/* 섹션 헤더 */}
      <Box
        sx={{
          p: 2.5,
          bgcolor: highlight ? '#fffbeb' : `${color}08`,
          borderBottom: '1px solid #e1e8ed',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                bgcolor: highlight ? '#f59e0b' : color,
                width: 32,
                height: 32,
              }}
            >
              {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 18 } })}
            </Avatar>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {title}
            </Typography>
          </Stack>
          
          {/* 🆕 수정 버튼 */}
          {onEdit && (
            <Button
              size="small"
              variant={highlight ? 'contained' : 'outlined'}
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              onClick={onEdit}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 'auto',
                ...(highlight ? {
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
                  },
                } : {
                  borderColor: color,
                  color: color,
                  '&:hover': {
                    borderColor: color,
                    bgcolor: `${color}08`,
                  },
                }),
                transition: 'all 0.2s ease',
              }}
            >
              {editLabel}
            </Button>
          )}
        </Stack>
      </Box>

      {/* 섹션 내용 */}
      <Box sx={{ p: 2.5 }}>
        {children}
      </Box>
    </CardContent>
  </Card>
);

// 📄 정보 항목 컴포넌트
interface InfoItemProps {
  label: string;
  value: string | number | React.ReactNode;
  icon?: React.ReactNode;
  copyable?: boolean; // 복사 가능 여부
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value, icon, copyable = false }) => {
  const [copied, setCopied] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  // 📋 복사 핸들러
  const handleCopy = async () => {
    if (!value || typeof value !== 'string') return;

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setSnackbarOpen(true);
      
      // 2초 후 체크 아이콘 리셋
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('복사 실패:', error);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
        {icon && (
          <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
            {value || '정보 없음'}
          </Typography>
        </Box>
        
        {/* 📋 복사 버튼 */}
        {copyable && value && (
          <Tooltip title={copied ? '복사됨!' : '클립보드에 복사'}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                bgcolor: copied ? 'success.main' : 'grey.100',
                color: copied ? 'white' : 'grey.600',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: copied ? 'success.dark' : 'grey.200',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <CopyIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* 📢 복사 완료 스낵바 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          클립보드에 복사되었습니다!
        </Alert>
      </Snackbar>
    </>
  );
};

// 🏦 은행 목록 (PurchaseForm과 동일)
const BANK_LIST = [
  { code: '001', name: '카카오뱅크' },
  { code: '002', name: '농협' },
  { code: '003', name: '국민은행' },
  { code: '004', name: '신한은행' },
  { code: '005', name: '우리은행' },
  { code: '006', name: 'IBK기업' },
  { code: '007', name: '하나은행' },
  { code: '008', name: '새마을금고' },
  { code: '009', name: '우체국' },
  { code: '010', name: '케이뱅크' },
  { code: '011', name: '토스뱅크' },
  { code: '012', name: '수협' },
  { code: '013', name: 'SC제일' },
  { code: '014', name: '산업' },
  { code: '015', name: '씨티' },
  { code: '016', name: '부산' },
  { code: '017', name: '신협' },
];

const ApplicationInfoTab: React.FC<ApplicationInfoTabProps> = ({ application, onUpdate }) => {
  // 🚚 택배송장 관리 상태
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingType, setTrackingType] = useState<'delivery' | 'return'>('delivery');
  const [trackingSubmitting, setTrackingSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // 📍 수거 주소 수정 상태
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressForm, setAddressForm] = useState({
    pickup_address: '',
    pickup_address_detail: '',
  });
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [isPostcodeOpen, setIsPostcodeOpen] = useState(false); // 다음 주소 검색 팝업 상태

  // 🏦 입금 계좌 수정 상태
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_holder: '',
    bank_account: '',
  });
  const [bankSubmitting, setBankSubmitting] = useState(false);

  // 🚚 택배송장 다이얼로그 열기
  const handleOpenTrackingDialog = (type: 'delivery' | 'return') => {
    setTrackingType(type);
    setTrackingNumber(
      type === 'delivery' 
        ? application.tracking_number || '' 
        : (application as any).return_tracking_number || ''
    );
    setTrackingDialogOpen(true);
  };

  // 🚚 택배송장 저장
  const handleSaveTracking = async () => {
    if (!application.id || !trackingNumber.trim()) return;
    setTrackingSubmitting(true);
    
    try {
      const ref = doc(db, 'applications', application.id);
      const updateData: any = {
        updated_at: serverTimestamp(),
      };
      
      if (trackingType === 'delivery') {
        updateData.tracking_number = trackingNumber.trim();
      } else {
        updateData.return_tracking_number = trackingNumber.trim();
      }
      
      await updateDoc(ref, updateData);
      
      setSnackbarMessage(`${trackingType === 'delivery' ? '택배송장' : '반품 택배송장'}번호가 저장되었습니다.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTrackingDialogOpen(false);
      
      // 🆕 화면 유지하면서 상태만 업데이트
      if (onUpdate) {
        const updatedApp = {
          ...application,
          ...(trackingType === 'delivery' 
            ? { tracking_number: trackingNumber.trim() }
            : { return_tracking_number: trackingNumber.trim() }
          ),
        };
        onUpdate(updatedApp);
      }
    } catch (error) {
      console.error('택배송장 저장 실패:', error);
      setSnackbarMessage('택배송장번호 저장에 실패했습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setTrackingSubmitting(false);
    }
  };

  // 📄 계약서 다운로드 핸들러
  const handleDownloadContract = async () => {
    if (!application.signature_data || !application.contract_terms_agreed) {
      setSnackbarMessage('계약서 정보가 없습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const pdfBlob = await generateContractPDF({
        application,
        signatureData: application.signature_data,
        contractTermsAgreed: application.contract_terms_agreed,
        contractDate: application.contract_signed_at ? new Date(application.contract_signed_at.seconds * 1000) : new Date(),
      });

      const filename = `매매계약서_${application.user_name}_${application.application_number}.pdf`;
      downloadPDF(pdfBlob, filename);
      
      setSnackbarMessage('계약서 PDF가 다운로드되었습니다.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('계약서 다운로드 실패:', error);
      setSnackbarMessage('계약서 다운로드에 실패했습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // 📍 수거 주소 다이얼로그 열기
  const handleOpenAddressDialog = () => {
    setAddressForm({
      pickup_address: application.pickup_address || '',
      pickup_address_detail: application.pickup_address_detail || '',
    });
    setAddressDialogOpen(true);
  };

  // 📍 수거 주소 저장 핸들러
  const handleSaveAddress = async () => {
    if (!application.id || !addressForm.pickup_address.trim()) {
      setSnackbarMessage('기본 주소는 필수 입력입니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setAddressSubmitting(true);

    try {
      const ref = doc(db, 'applications', application.id);
      await updateDoc(ref, {
        pickup_address: addressForm.pickup_address.trim(),
        pickup_address_detail: addressForm.pickup_address_detail.trim(),
        // 정보가 완성되었으면 is_incomplete 플래그 업데이트
        is_incomplete: !(addressForm.pickup_address.trim() && application.bank_name && application.bank_account),
        updated_at: serverTimestamp(),
      });

      setSnackbarMessage('수거 주소가 성공적으로 저장되었습니다.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setAddressDialogOpen(false);

      // 🆕 화면 유지하면서 상태만 업데이트
      if (onUpdate) {
        const updatedApp = {
          ...application,
          pickup_address: addressForm.pickup_address.trim(),
          pickup_address_detail: addressForm.pickup_address_detail.trim(),
          is_incomplete: !(addressForm.pickup_address.trim() && application.bank_name && application.bank_account),
        };
        onUpdate(updatedApp);
      }
    } catch (error) {
      console.error('수거 주소 저장 실패:', error);
      setSnackbarMessage('수거 주소 저장에 실패했습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setAddressSubmitting(false);
    }
  };

  // 🏦 입금 계좌 다이얼로그 열기
  const handleOpenBankDialog = () => {
    setBankForm({
      bank_name: application.bank_name || '',
      account_holder: application.user_name || '', // 예금주는 신청자 이름으로 고정
      bank_account: application.bank_account || '',
    });
    setBankDialogOpen(true);
  };

  // 🏦 입금 계좌 저장 핸들러
  const handleSaveBank = async () => {
    if (!application.id) {
      setSnackbarMessage('신청 정보를 찾을 수 없습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // 필수 필드 검증
    if (!bankForm.bank_name.trim()) {
      setSnackbarMessage('은행명을 선택해주세요.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // 예금주는 신청자 이름으로 자동 설정되므로 별도 검증 불필요
    // (신청자 이름이 없는 경우는 발생하지 않음)

    if (!bankForm.bank_account.trim()) {
      setSnackbarMessage('계좌번호를 입력해주세요.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // 계좌번호 형식 검증 (숫자만)
    const accountNumberClean = bankForm.bank_account.replace(/[^0-9]/g, '');
    if (accountNumberClean.length < 10 || accountNumberClean.length > 16) {
      setSnackbarMessage('계좌번호는 10~16자리의 숫자여야 합니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setBankSubmitting(true);

    try {
      const ref = doc(db, 'applications', application.id);
      await updateDoc(ref, {
        bank_name: bankForm.bank_name.trim(),
        account_holder: bankForm.account_holder.trim(),
        bank_account: accountNumberClean, // 숫자만 저장
        // 정보가 완성되었으면 is_incomplete 플래그 업데이트
        is_incomplete: !(application.pickup_address && bankForm.bank_name.trim() && accountNumberClean),
        updated_at: serverTimestamp(),
      });

      setSnackbarMessage('입금 계좌 정보가 성공적으로 저장되었습니다.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setBankDialogOpen(false);

      // 🆕 화면 유지하면서 상태만 업데이트
      if (onUpdate) {
        const updatedApp = {
          ...application,
          bank_name: bankForm.bank_name.trim(),
          account_holder: bankForm.account_holder.trim(),
          bank_account: accountNumberClean,
          is_incomplete: !(application.pickup_address && bankForm.bank_name.trim() && accountNumberClean),
        };
        onUpdate(updatedApp);
      }
    } catch (error) {
      console.error('입금 계좌 저장 실패:', error);
      setSnackbarMessage('입금 계좌 저장에 실패했습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setBankSubmitting(false);
    }
  };

  // 🆕 수동 신청 여부 및 정보 미완성 체크
  const isManualApplication = application.source_type === 'manual' || application.source_type === 'partner';
  const isIncomplete = application.is_incomplete === true;
  const needsAddress = !application.pickup_address || application.pickup_address.trim() === '';
  const needsBank = !application.bank_name || !application.bank_account;
  const partnerInfo = application.partner_info;

  return (
    <Box>
      <Grid container spacing={3}>
        {/* 🆕 수동 신청 및 거래처 정보 (수동 신청인 경우에만 표시) */}
        {isManualApplication && (
          <Grid item xs={12}>
            <InfoSection
              title={application.source_type === 'partner' ? '거래처 유입 정보' : '수동 신청 정보'}
              icon={<BusinessIcon />}
              color="#7c3aed"
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.05) 0%, rgba(139, 92, 246, 0.08) 100%)',
                  border: '1px solid rgba(124, 58, 237, 0.15)',
                }}
              >
                <Grid container spacing={2}>
                  {/* 유입 경로 */}
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="유입 경로"
                      value={
                        <Chip
                          label={application.source_type === 'partner' ? '거래처 유입' : '관리자 수동 등록'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: application.source_type === 'partner' ? '#7c3aed' : '#6366f1',
                            color: '#fff',
                          }}
                        />
                      }
                      icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                    />
                  </Grid>
                  
                  {/* 정보 완성 상태 */}
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="정보 상태"
                      value={
                        <Chip
                          label={isIncomplete ? '정보 미완성' : '정보 완성'}
                          size="small"
                          color={isIncomplete ? 'warning' : 'success'}
                          icon={isIncomplete ? <WarningIcon sx={{ fontSize: 16 }} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                          sx={{ fontWeight: 600 }}
                        />
                      }
                    />
                  </Grid>
                  
                  {/* 거래처명 */}
                  {partnerInfo?.partner_name && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoItem
                        label="거래처명"
                        value={partnerInfo.partner_name}
                        icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                      />
                    </Grid>
                  )}
                  
                  {/* 거래처 주문번호 */}
                  {partnerInfo?.order_number && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoItem
                        label="거래처 주문번호"
                        value={partnerInfo.order_number}
                        copyable={true}
                      />
                    </Grid>
                  )}
                  
                  {/* 정보 입력 토큰 */}
                  {application.info_token_id && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoItem
                        label="정보 입력 링크"
                        value={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                              label="발송됨"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: 11 }}
                            />
                            <Tooltip title="고객 정보 입력 페이지 열기">
                              <IconButton
                                size="small"
                                onClick={() => window.open(`https://event.dwt.kr/customer/complete-info?token=${application.info_token_id}`, '_blank')}
                                sx={{ 
                                  width: 28, 
                                  height: 28,
                                  color: '#6366f1',
                                }}
                              >
                                <LinkIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        }
                        icon={<LinkIcon sx={{ fontSize: 18 }} />}
                      />
                    </Grid>
                  )}
                  
                  {/* 등록한 관리자 */}
                  {partnerInfo?.created_by && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoItem
                        label="등록 관리자"
                        value={partnerInfo.created_by}
                      />
                    </Grid>
                  )}
                </Grid>
                
                {/* 거래처 메모 */}
                {partnerInfo?.memo && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(124, 58, 237, 0.15)' }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                      <NotesIcon sx={{ fontSize: 18, color: '#7c3aed', mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          거래처 메모
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                          {partnerInfo.memo}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Box>
            </InfoSection>
          </Grid>
        )}

        {/* 📈 마케팅 유입경로 (수집된 경우에만 표시) */}
        {application.inflow && (
          <Grid item xs={12}>
            <InfoSection
              title="마케팅 유입경로"
              icon={<CampaignIcon />}
              color="#0ea5e9"
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <InfoItem
                    label="유입 채널"
                    value={
                      <Chip
                        label={getInflowLabel(application.inflow.source)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: getInflowColor(application.inflow.source),
                          color: '#fff',
                        }}
                      />
                    }
                    icon={<CampaignIcon sx={{ fontSize: 18 }} />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <InfoItem
                    label="매체 (utm_medium)"
                    value={application.inflow.medium || '-'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <InfoItem
                    label="캠페인 (utm_campaign)"
                    value={application.inflow.campaign || '-'}
                  />
                </Grid>
                {application.inflow.content && (
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoItem
                      label="콘텐츠 (utm_content)"
                      value={application.inflow.content}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3}>
                  <InfoItem
                    label="유입 Referrer"
                    value={application.inflow.referrer || '-'}
                    copyable={Boolean(application.inflow.referrer)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <InfoItem
                    label="랜딩 페이지"
                    value={application.inflow.landing_page || '-'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <InfoItem
                    label="수집 시각"
                    value={
                      application.inflow.captured_at
                        ? new Date(application.inflow.captured_at).toLocaleString('ko-KR')
                        : '-'
                    }
                    icon={<CalendarIcon sx={{ fontSize: 18 }} />}
                  />
                </Grid>
              </Grid>
            </InfoSection>
          </Grid>
        )}

        {/* 👤 신청자 정보 */}
        <Grid item xs={12} lg={6}>
          <InfoSection
            title="신청자 정보"
            icon={<PersonIcon />}
            color="#1976d2"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label="이름"
                  value={application.user_name}
                  icon={<PersonIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label="전화번호"
                  value={application.phone_number}
                  icon={<PhoneIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label="본인인증"
                  value={
                    <Chip
                      label={application.auth_verified ? '인증완료' : '미인증'}
                      size="small"
                      color={application.auth_verified ? 'success' : 'error'}
                      icon={<SecurityIcon sx={{ fontSize: 16 }} />}
                      sx={{ fontWeight: 600 }}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label="개인정보 동의"
                  value={
                    <Chip
                      label={application.agreed_to_privacy ? '동의함' : '미동의'}
                      size="small"
                      color={application.agreed_to_privacy ? 'success' : 'error'}
                      icon={<PrivacyIcon sx={{ fontSize: 16 }} />}
                      sx={{ fontWeight: 600 }}
                    />
                  }
                />
              </Grid>
            </Grid>
          </InfoSection>
        </Grid>

        {/* 📍 주소 정보 */}
        <Grid item xs={12} lg={6}>
          <InfoSection
            title="수거 주소"
            icon={<LocationIcon />}
            color="#4caf50"
            highlight={needsAddress}
            highlightMessage={needsAddress ? '⚠️ 수거 주소가 입력되지 않았습니다. 정보를 입력해주세요.' : undefined}
            onEdit={handleOpenAddressDialog}
            editLabel={needsAddress ? '주소 입력' : '수정'}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <InfoItem
                  label="기본 주소"
                  value={application.pickup_address}
                  icon={<LocationIcon sx={{ fontSize: 18 }} />}
                  copyable={true} // 복사 가능
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoItem
                  label="매장 코드"
                  value={application.store_code || '온라인 신청'}
                />
              </Grid>
              <Grid item xs={12}>
                <InfoItem
                  label="상세 주소"
                  value={application.pickup_address_detail}
                  copyable={true} // 복사 가능
                />
              </Grid>
            </Grid>
          </InfoSection>
        </Grid>

        {/* 🏦 계좌 정보 */}
        <Grid item xs={12} lg={6}>
          <InfoSection
            title="입금 계좌"
            icon={<BankIcon />}
            color="#ff9800"
            highlight={needsBank}
            highlightMessage={needsBank ? '⚠️ 입금 계좌 정보가 입력되지 않았습니다. 정보를 입력해주세요.' : undefined}
            onEdit={handleOpenBankDialog}
            editLabel={needsBank ? '계좌 입력' : '수정'}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label="은행명"
                  value={application.bank_name}
                  icon={<BankIcon sx={{ fontSize: 18 }} />}
                  copyable={true} // 복사 가능
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label="예금주"
                  value={application.account_holder}
                  copyable={true} // 복사 가능
                />
              </Grid>
              <Grid item xs={12}>
                <InfoItem
                  label="계좌번호"
                  value={application.bank_account}
                  copyable={true} // 복사 가능
                />
              </Grid>
            </Grid>
          </InfoSection>
        </Grid>

        {/* 📦 택배송장 정보 */}
        <Grid item xs={12} lg={6}>
          <InfoSection
            title="택배송장 정보"
            icon={<ShippingIcon />}
            color="#ff9800"
          >
            <Grid container spacing={2}>
              {/* 발송 택배송장 */}
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label="발송 송장번호"
                  value={
                    application.tracking_number ? (
                      <Stack direction="column" alignItems="flex-start" spacing={1}>
                        <Chip
                          label={application.tracking_number}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontWeight: 600,
                            bgcolor: '#e3f2fd',
                            color: '#1565c0',
                            borderColor: '#90caf9'
                          }}
                        />
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="배송조회">
                            <IconButton
                              size="small"
                              onClick={() => window.open(`https://tracker.delivery/#/kr.cjlogistics/${application.tracking_number}`, '_blank')}
                              sx={{
                                width: 28,
                                height: 28,
                                color: '#1565c0',
                                '&:hover': {
                                  bgcolor: 'rgba(21, 101, 192, 0.04)',
                                }
                              }}
                            >
                              <LaunchIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="수정">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenTrackingDialog('delivery')}
                              sx={{
                                width: 28,
                                height: 28,
                                color: '#757575',
                                '&:hover': {
                                  bgcolor: 'rgba(117, 117, 117, 0.04)',
                                  color: '#424242',
                                }
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack direction="column" alignItems="flex-start" spacing={1}>
                        <Chip
                          label="미입력"
                          size="small"
                          variant="outlined"
                          sx={{ 
                            color: '#757575',
                            borderColor: '#e0e0e0',
                            bgcolor: '#fafafa'
                          }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenTrackingDialog('delivery')}
                          sx={{
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            borderColor: '#1565c0',
                            color: '#1565c0',
                            '&:hover': {
                              borderColor: '#0d47a1',
                              color: '#0d47a1',
                              bgcolor: 'rgba(21, 101, 192, 0.04)',
                            }
                          }}
                        >
                          입력
                        </Button>
                      </Stack>
                    )
                  }
                  icon={<ShippingIcon sx={{ fontSize: 18, color: '#1565c0' }} />}
                />
              </Grid>

              {/* 반품 택배송장 */}
              <Grid item xs={12} sm={6}>
                <InfoItem
                  label="반품 송장번호"
                  value={
                    (application.status === '반송' || (application as any).return_tracking_number) ? (
                      (application as any).return_tracking_number ? (
                        <Stack direction="column" alignItems="flex-start" spacing={1}>
                          <Chip
                            label={(application as any).return_tracking_number}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              fontWeight: 600,
                              bgcolor: '#ffebee',
                              color: '#c62828',
                              borderColor: '#ef9a9a'
                            }}
                          />
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="배송조회">
                              <IconButton
                                size="small"
                                onClick={() => window.open(`https://tracker.delivery/#/kr.cjlogistics/${(application as any).return_tracking_number}`, '_blank')}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  color: '#c62828',
                                  '&:hover': {
                                    bgcolor: 'rgba(198, 40, 40, 0.04)',
                                  }
                                }}
                              >
                                <LaunchIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="수정">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenTrackingDialog('return')}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  color: '#757575',
                                  '&:hover': {
                                    bgcolor: 'rgba(117, 117, 117, 0.04)',
                                    color: '#424242',
                                  }
                                }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      ) : (
                        <Stack direction="column" alignItems="flex-start" spacing={1}>
                          <Chip
                            label="미입력"
                            size="small"
                            variant="outlined"
                            sx={{ 
                              color: '#757575',
                              borderColor: '#e0e0e0',
                              bgcolor: '#fafafa'
                            }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenTrackingDialog('return')}
                            sx={{
                              fontSize: '0.75rem',
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5,
                              borderColor: '#c62828',
                              color: '#c62828',
                              '&:hover': {
                                borderColor: '#b71c1c',
                                color: '#b71c1c',
                                bgcolor: 'rgba(198, 40, 40, 0.04)',
                              }
                            }}
                          >
                            입력
                          </Button>
                        </Stack>
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        반송 시 표시
                      </Typography>
                    )
                  }
                  icon={<ShippingIcon sx={{ fontSize: 18, color: '#c62828' }} />}
                />
              </Grid>
            </Grid>
          </InfoSection>
        </Grid>

        {/* 📱 기기 요약 */}
        <Grid item xs={12} lg={6}>
          <InfoSection
            title="신청 기기"
            icon={<DeviceIcon />}
            color="#9c27b0"
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InfoItem
                  label="총 기기 수"
                  value={`${application.devices?.length || 0}개`}
                  icon={<DeviceIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>
              
              {application.devices && application.devices.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                      기기 목록
                    </Typography>
                  </Grid>
                  {application.devices.map((device, index) => (
                    <Grid item xs={12} sm={6} key={device.id}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid #e1e8ed',
                          height: '100%',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            sx={{
                              bgcolor: 'primary.main',
                              width: 24,
                              height: 24,
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {index + 1}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {device.deviceModel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {device.deviceType}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
          </InfoSection>
        </Grid>

        {/* 📊 신청 상태 */}
        <Grid item xs={12}>
          <InfoSection
            title="신청 현황"
            icon={<ReceiptIcon />}
            color="#f44336"
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoItem
                  label="신청번호"
                  value={`#${application.application_number}`}
                  icon={<ReceiptIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoItem
                  label="신청일시"
                  value={formatDate(application.created_at)}
                  icon={<CalendarIcon sx={{ fontSize: 18 }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoItem
                  label="최종 수정일"
                  value={application.updated_at ? formatDate(application.updated_at) : '수정 없음'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoItem
                  label="담당자"
                  value="미지정"
                />
              </Grid>
            </Grid>
          </InfoSection>
        </Grid>

        {/* 📝 추가 정보 */}
        {application.remark && (
          <Grid item xs={12}>
            <InfoSection
              title="관리자 메모"
              icon={<ReceiptIcon />}
              color="#607d8b"
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid #e1e8ed',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {application.remark}
                </Typography>
              </Box>
            </InfoSection>
          </Grid>
        )}

        {/* 📄 매매 계약서 - 스마트폰/태블릿 전용 */}
        {isContractFeatureAvailable(application.devices || []) && (
          <Grid item xs={12}>
            <InfoSection
              title="매매 계약서"
              icon={<ContractIcon />}
              color="#059669"
            >
              {(() => {
                const devices = application.devices || [];
                // 계약 대상 기기: 스마트폰/태블릿이면서 contract_signed_at이 있는 기기만
                const contractDevices = devices.filter(d => {
                  const hasContract = !!(d as any).contract_signed_at;
                  const isContractEligible = !isWearable(d.deviceType); // 웨어러블이 아닌 경우만
                  return hasContract && isContractEligible;
                });
                const hasGlobalContract = !!(application as any).contract_signed_at;
                
                // 전체 계약서가 있거나, 계약 대상 기기별 계약이 하나라도 있으면 표시
                if (!hasGlobalContract && contractDevices.length === 0) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        아직 체결된 계약서가 없습니다
                      </Typography>
                    </Box>
                  );
                }
                
                return (
                  <Grid container spacing={3}>
                    {/* 전체 계약서 (기존) */}
                    {hasGlobalContract && (
                      <>
                        <Grid item xs={12}>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.08) 0%, rgba(16, 185, 129, 0.06) 100%)',
                            border: '1px solid rgba(5, 150, 105, 0.2)'
                          }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#059669' }}>
                              📋 전체 신청서 계약
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoItem
                            label="계약 체결일"
                            value={formatDate(new Date((application as any).contract_signed_at.seconds * 1000))}
                            icon={<CalendarIcon sx={{ fontSize: 18 }} />}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoItem
                            label="계약 상태"
                            value={
                              <Chip
                                label={application.contract_status === 'completed' ? '체결완료' : '진행중'}
                                color={application.contract_status === 'completed' ? 'success' : 'warning'}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoItem
                            label="서명 여부"
                            value={
                              <Chip
                                label={application.signature_data ? '서명완료' : '서명없음'}
                                color={application.signature_data ? 'success' : 'default'}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <InfoItem
                            label="전체 계약서"
                            value={
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadContract}
                                disabled={!application.signature_data || !application.contract_terms_agreed}
                                sx={{
                                  borderRadius: 2,
                                  px: 2,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  borderColor: '#059669',
                                  color: '#059669',
                                  '&:hover': {
                                    borderColor: '#047857',
                                    color: '#047857',
                                    bgcolor: 'rgba(5, 150, 105, 0.04)',
                                  },
                                  '&.Mui-disabled': {
                                    borderColor: '#e5e7eb',
                                    color: '#9ca3af',
                                  }
                                }}
                              >
                                PDF 다운로드
                              </Button>
                            }
                          />
                        </Grid>
                      </>
                    )}
                    
                    {/* 기기별 계약서 */}
                    {contractDevices.length > 0 && (
                      <>
                        <Grid item xs={12}>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(14, 165, 233, 0.06) 100%)',
                            border: '1px solid rgba(99, 102, 241, 0.2)'
                          }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#6366f1' }}>
                              📱 기기별 계약서 ({contractDevices.length}건)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              각 기기별로 개별 계약서를 다운로드할 수 있습니다
                            </Typography>
                          </Box>
                        </Grid>
                        
                        {contractDevices.map((device, index) => {
                          const contractDate = (device as any).contract_signed_at 
                            ? new Date(typeof (device as any).contract_signed_at.seconds !== 'undefined' 
                                ? (device as any).contract_signed_at.seconds * 1000 
                                : (device as any).contract_signed_at)
                            : new Date();
                          
                          return (
                            <Grid item xs={12} sm={6} key={device.id}>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: 3,
                                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                                  border: '1px solid rgba(99, 102, 241, 0.1)',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.15)',
                                  },
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    background: 'linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)',
                                  }
                                }}
                              >
                                <Stack spacing={2}>
                                  <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Avatar
                                      sx={{
                                        background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                                        width: 32,
                                        height: 32,
                                        fontSize: 12,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {index + 1}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1f2937' }}>
                                        {device.deviceModel}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {device.deviceType} · {(device as any).price_offer ? `${Number((device as any).price_offer).toLocaleString()}원` : '가격미정'}
                                      </Typography>
                                    </Box>
                                    <Chip
                                      label="계약완료"
                                      size="small"
                                      sx={{
                                        bgcolor: '#10b981',
                                        color: '#fff',
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                      }}
                                    />
                                  </Stack>
                                  
                                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        체결일시
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600}>
                                        {formatDate(contractDate)}
                                      </Typography>
                                    </Box>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<DownloadIcon />}
                                      onClick={async () => {
                                        try {
                                          const pdfBlob = await generateContractPDF({
                                            application,
                                            signatureData: application.signature_data || '',
                                            contractTermsAgreed: application.contract_terms_agreed || {},
                                            contractDate,
                                            deviceId: device.id, // 기기별 계약서
                                          });
                                          
                                          const filename = `매매계약서_${application.user_name}_${device.deviceModel}_${application.application_number}.pdf`;
                                          downloadPDF(pdfBlob, filename);
                                          
                                          setSnackbarMessage(`${device.deviceModel} 계약서가 다운로드되었습니다.`);
                                          setSnackbarSeverity('success');
                                          setSnackbarOpen(true);
                                        } catch (error) {
                                          console.error('기기별 계약서 다운로드 실패:', error);
                                          setSnackbarMessage('계약서 다운로드에 실패했습니다.');
                                          setSnackbarSeverity('error');
                                          setSnackbarOpen(true);
                                        }
                                      }}
                                      sx={{
                                        borderRadius: 2,
                                        px: 2,
                                        py: 0.5,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                        boxShadow: '0 3px 12px rgba(5, 150, 105, 0.25)',
                                        '&:hover': {
                                          background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                                          boxShadow: '0 4px 16px rgba(5, 150, 105, 0.35)',
                                          transform: 'translateY(-1px)',
                                        },
                                        transition: 'all 0.2s ease',
                                      }}
                                    >
                                      PDF 다운로드
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </>
                    )}
                    
                    {/* 전체 계약 조항 동의 현황 (전체 계약서가 있는 경우에만) */}
                    {hasGlobalContract && application.contract_terms_agreed && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                          계약 조항 동의 현황
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {Object.entries(application.contract_terms_agreed).map(([termId, agreed]) => (
                            <Chip
                              key={termId}
                              label={termId}
                              color={agreed ? 'success' : 'default'}
                              size="small"
                              variant={agreed ? 'filled' : 'outlined'}
                              sx={{ 
                                fontSize: '0.7rem',
                                height: 24,
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                );
              })()}
            </InfoSection>
          </Grid>
        )}
      </Grid>

      {/* 🚚 택배송장 입력 다이얼로그 */}
      <Dialog 
        open={trackingDialogOpen} 
        onClose={() => setTrackingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: trackingType === 'delivery' 
              ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(14, 165, 233, 0.06) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(251, 146, 60, 0.06) 100%)',
            borderBottom: `1px solid ${trackingType === 'delivery' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(239, 68, 68, 0.1)'}`,
            fontWeight: 700,
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {trackingType === 'delivery' ? '📦 택배송장번호 관리' : '📦 반품송장번호 관리'}
          </Typography>
          <IconButton 
            onClick={() => setTrackingDialogOpen(false)}
            size="small"
            sx={{ color: '#6b7280' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.6 }}>
            {trackingType === 'delivery' 
              ? '고객이 기기를 발송한 택배송장번호를 입력해주세요. CJ대한통운 송장번호를 권장합니다.'
              : '고객에게 반품 발송한 택배송장번호를 입력해주세요.'}
          </Typography>
          <TextField
            autoFocus
            label={trackingType === 'delivery' ? '택배송장번호' : '반품송장번호'}
            fullWidth
            variant="outlined"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="예: 123456789012"
            helperText="숫자만 입력해주세요 (하이픈 제외)"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: trackingType === 'delivery' ? '#6366f1' : '#ef4444',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: trackingType === 'delivery' ? '#6366f1' : '#ef4444',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setTrackingDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1,
              borderColor: 'rgba(107, 114, 128, 0.3)',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#6b7280',
                bgcolor: 'rgba(107, 114, 128, 0.05)',
              }
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleSaveTracking}
            variant="contained"
            disabled={!trackingNumber.trim() || trackingSubmitting}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1,
              fontWeight: 700,
              background: trackingType === 'delivery' 
                ? 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
              boxShadow: trackingType === 'delivery'
                ? '0 4px 16px rgba(99, 102, 241, 0.3)'
                : '0 4px 16px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                background: trackingType === 'delivery'
                  ? 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)'
                  : 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
                boxShadow: trackingType === 'delivery'
                  ? '0 6px 20px rgba(99, 102, 241, 0.4)'
                  : '0 6px 20px rgba(239, 68, 68, 0.4)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%)',
                color: '#9ca3af',
              }
            }}
          >
            {trackingSubmitting ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 📍 수거 주소 수정 다이얼로그 */}
      <Dialog
        open={addressDialogOpen}
        onClose={() => !addressSubmitting && setAddressDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(76, 175, 80, 0.15)',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(129, 199, 132, 0.08) 100%)',
            borderBottom: '1px solid rgba(76, 175, 80, 0.15)',
            py: 2.5,
            px: 3,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                  width: 44,
                  height: 44,
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                }}
              >
                <HomeIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#1b5e20">
                  수거 주소 수정
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  신청번호 #{application.application_number} · {application.user_name}
                </Typography>
              </Box>
            </Stack>
            <IconButton
              onClick={() => !addressSubmitting && setAddressDialogOpen(false)}
              disabled={addressSubmitting}
              sx={{
                color: '#6b7280',
                '&:hover': { bgcolor: 'rgba(107, 114, 128, 0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 4, pb: 2, px: 3 }}>
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: 'rgba(76, 175, 80, 0.06)',
              border: '1px solid rgba(76, 175, 80, 0.12)',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <LocationIcon sx={{ fontSize: 20, color: '#4caf50' }} />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                고객의 기기를 수거할 주소를 입력해주세요. '주소 검색' 버튼을 클릭하여 정확한 주소를 선택하세요.
              </Typography>
            </Stack>
          </Box>

          <Grid container spacing={3}>
            {/* 기본 주소 + 검색 버튼 */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <TextField
                  label="기본 주소"
                  fullWidth
                  required
                  variant="outlined"
                  value={addressForm.pickup_address}
                  placeholder="주소 검색 버튼을 클릭하세요"
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f8fafc',
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#4caf50',
                          borderWidth: 2,
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#4caf50',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => setIsPostcodeOpen(true)}
                  startIcon={<SearchIcon />}
                  sx={{
                    minWidth: 140,
                    height: 56,
                    borderRadius: 3,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.25)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                      boxShadow: '0 6px 16px rgba(76, 175, 80, 0.35)',
                    },
                  }}
                >
                  주소 검색
                </Button>
              </Stack>
            </Grid>

            {/* 상세 주소 */}
            <Grid item xs={12}>
              <TextField
                label="상세 주소"
                fullWidth
                variant="outlined"
                value={addressForm.pickup_address_detail}
                onChange={(e) => setAddressForm(prev => ({ ...prev, pickup_address_detail: e.target.value }))}
                placeholder="예: 101동 2001호, 리퍼브바이 빌딩 3층"
                helperText="아파트 동/호수, 건물 내 상세 위치 등을 입력해주세요"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeIcon sx={{ fontSize: 20, color: '#81c784' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: '#fff',
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4caf50',
                        borderWidth: 2,
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4caf50',
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* 기존 정보 미리보기 */}
          {(application.pickup_address || application.pickup_address_detail) && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }}>
                <Chip label="기존 정보" size="small" variant="outlined" />
              </Divider>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  border: '1px dashed #e1e8ed',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                  현재 등록된 주소
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {application.pickup_address || '(기본 주소 없음)'}
                  {application.pickup_address_detail && `, ${application.pickup_address_detail}`}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, bgcolor: 'rgba(248, 250, 252, 0.8)', gap: 1.5 }}>
          <Button
            onClick={() => setAddressDialogOpen(false)}
            disabled={addressSubmitting}
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              borderColor: 'rgba(107, 114, 128, 0.3)',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#6b7280',
                bgcolor: 'rgba(107, 114, 128, 0.05)',
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleSaveAddress}
            disabled={!addressForm.pickup_address.trim() || addressSubmitting}
            variant="contained"
            startIcon={addressSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
              boxShadow: '0 4px 16px rgba(76, 175, 80, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.45)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%)',
                color: '#9ca3af',
                boxShadow: 'none',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {addressSubmitting ? '저장 중...' : '주소 저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 📍 다음 주소 검색 다이얼로그 */}
      <Dialog 
        open={isPostcodeOpen} 
        onClose={() => setIsPostcodeOpen(false)} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(129, 199, 132, 0.08) 100%)',
            borderBottom: '1px solid rgba(76, 175, 80, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SearchIcon sx={{ color: '#4caf50' }} />
            <Typography variant="h6" fontWeight={700} color="#1b5e20">
              주소 검색
            </Typography>
          </Stack>
          <IconButton onClick={() => setIsPostcodeOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {isPostcodeOpen && (
            <Box sx={{ height: 450 }}>
              <DaumPostcode 
                onComplete={(data) => {
                  setAddressForm(prev => ({ ...prev, pickup_address: data.address }));
                  setIsPostcodeOpen(false);
                }} 
                style={{ width: '100%', height: '100%' }} 
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* 🏦 입금 계좌 수정 다이얼로그 */}
      <Dialog
        open={bankDialogOpen}
        onClose={() => !bankSubmitting && setBankDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 248, 241, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 152, 0, 0.15)',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 183, 77, 0.08) 100%)',
            borderBottom: '1px solid rgba(255, 152, 0, 0.15)',
            py: 2.5,
            px: 3,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                  width: 44,
                  height: 44,
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                }}
              >
                <BankIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#e65100">
                  입금 계좌 수정
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  신청번호 #{application.application_number} · {application.user_name}
                </Typography>
              </Box>
            </Stack>
            <IconButton
              onClick={() => !bankSubmitting && setBankDialogOpen(false)}
              disabled={bankSubmitting}
              sx={{
                color: '#6b7280',
                '&:hover': { bgcolor: 'rgba(107, 114, 128, 0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 4, pb: 2, px: 3 }}>
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: 'rgba(255, 152, 0, 0.06)',
              border: '1px solid rgba(255, 152, 0, 0.12)',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <BankIcon sx={{ fontSize: 20, color: '#ff9800' }} />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                매입 금액을 입금받을 계좌 정보를 입력해주세요. 예금주명은 신청자와 동일해야 합니다.
              </Typography>
            </Stack>
          </Box>

          <Grid container spacing={3}>
            {/* 은행명 선택 (Autocomplete - PurchaseForm과 동일) */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={BANK_LIST}
                getOptionLabel={(option) => option.name}
                value={BANK_LIST.find(b => b.name === bankForm.bank_name) || null}
                onChange={(_, newValue) =>
                  setBankForm(prev => ({ ...prev, bank_name: newValue ? newValue.name : '' }))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar 
                        sx={{ 
                          width: 28, 
                          height: 28, 
                          bgcolor: 'rgba(255, 152, 0, 0.15)',
                          color: '#ff9800',
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {option.name.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>
                        {option.name}
                      </Typography>
                    </Stack>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="은행 선택"
                    required
                    placeholder="은행을 선택하세요"
                    helperText="입금받을 은행을 선택해주세요"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <BankIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        bgcolor: '#fff',
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#ff9800',
                            borderWidth: 2,
                          },
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#ff9800',
                      },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                clearOnEscape
                ListboxProps={{
                  sx: {
                    maxHeight: 280,
                    '& .MuiAutocomplete-option': {
                      py: 1.5,
                      '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.08)' },
                      '&[aria-selected="true"]': { 
                        bgcolor: 'rgba(255, 152, 0, 0.12)',
                        '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.16)' },
                      },
                    },
                  },
                }}
              />
            </Grid>

            {/* 예금주 (수정 불가 - 신청자 이름 자동 적용) */}
            <Grid item xs={12} md={6}>
              <TextField
                label="예금주"
                fullWidth
                required
                variant="outlined"
                value={bankForm.account_holder}
                disabled
                placeholder="예: 홍길동"
                helperText="신청자 이름으로 자동 설정됩니다"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: '#f5f5f5',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#9ca3af',
                  },
                }}
              />
            </Grid>

            {/* 계좌번호 */}
            <Grid item xs={12}>
              <TextField
                label="계좌번호"
                fullWidth
                required
                variant="outlined"
                value={bankForm.bank_account}
                onChange={(e) => {
                  // 숫자만 허용하되, 사용자 편의를 위해 하이픈도 입력 허용 (저장 시 제거)
                  const value = e.target.value.replace(/[^0-9-]/g, '');
                  setBankForm(prev => ({ ...prev, bank_account: value }));
                }}
                placeholder="예: 110-123-456789"
                helperText="계좌번호를 입력해주세요 (숫자만 입력, 하이픈은 자동 제거됩니다)"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreditCardIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: '#fff',
                    fontFamily: '"Roboto Mono", monospace',
                    letterSpacing: 1,
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ff9800',
                        borderWidth: 2,
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#ff9800',
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* 기존 정보 미리보기 */}
          {(application.bank_name || application.bank_account) && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }}>
                <Chip label="기존 정보" size="small" variant="outlined" />
              </Divider>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#fffbf5',
                  border: '1px dashed rgba(255, 152, 0, 0.3)',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                      은행
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {application.bank_name || '(미입력)'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                      예금주
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {application.account_holder || '(미입력)'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                      계좌번호
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                      {application.bank_account || '(미입력)'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, bgcolor: 'rgba(255, 248, 241, 0.8)', gap: 1.5 }}>
          <Button
            onClick={() => setBankDialogOpen(false)}
            disabled={bankSubmitting}
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              borderColor: 'rgba(107, 114, 128, 0.3)',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#6b7280',
                bgcolor: 'rgba(107, 114, 128, 0.05)',
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleSaveBank}
            disabled={!bankForm.bank_name || !bankForm.bank_account.trim() || bankSubmitting}
            variant="contained"
            startIcon={bankSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
              boxShadow: '0 4px 16px rgba(255, 152, 0, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
                boxShadow: '0 6px 20px rgba(255, 152, 0, 0.45)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%)',
                color: '#9ca3af',
                boxShadow: 'none',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {bankSubmitting ? '저장 중...' : '계좌 저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 📢 알림 스낵바 */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          sx={{ 
            width: '100%',
            borderRadius: 3,
            fontWeight: 600,
          }}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApplicationInfoTab;